/**
 * gammaHf — модель 6-слотовых колонтитулов Gamma (владелец 2026-08-14
 * «мало колонтитулов»: раньше UI позволял максимум три элемента — один
 * текст, один номер, один логотип; API же позволяет заполнить все 6 слотов
 * кромки, в т.ч. несколькими разными текстами).
 *
 * Здесь: типы слотов, конвертер настроек (включая ЛЕГАСИ-поля
 * gammaFooterText/Number/… — сохранённые колонтитулы юзеров не пропадают),
 * патч обратно в настройки и сводка. Общий код для шита, превью-стола,
 * сводки пилюли и payload'а.
 */
import type { GammaHfPosition, GammaHfSlots } from './gammaOptions'
import type { PresentationSettings } from '../hooks/useGenerateSettings'

export interface GammaHfState {
	slots: GammaHfSlots
	/** Один загруженный логотип на все logo-слоты (ключ S3 + превью-URL). */
	logoKey: string
	logoUrl: string
	logoSize: 'sm' | 'md' | 'lg' | 'xl'
	hideFirst: boolean
	hideLast: boolean
}

/** Текущее состояние колонтитулов из настроек. Приоритет — новая карта
 *  gammaHfSlots; её отсутствие значит «настройки из старой версии» —
 *  собираем слоты из легаси-полей (текст/номер/лого по одной штуке). */
export function hfStateFromSettings(pres: PresentationSettings | undefined): GammaHfState {
	const slots: GammaHfSlots = {}
	if (pres?.gammaHfSlots) {
		for (const [pos, content] of Object.entries(pres.gammaHfSlots)) {
			if (content) slots[pos as GammaHfPosition] = content
		}
	} else if (pres) {
		if (pres.gammaFooterText) {
			slots[pres.gammaFooterTextPos ?? 'bottomLeft'] = {
				type: 'text',
				value: pres.gammaFooterText,
			}
		}
		if (pres.gammaFooterNumber) {
			slots[pres.gammaFooterNumberPos ?? 'bottomRight'] = { type: 'number' }
		}
		if (pres.gammaFooterLogoKey) {
			slots[pres.gammaFooterLogoPos ?? 'topLeft'] = { type: 'logo' }
		}
	}
	return {
		slots,
		logoKey: pres?.gammaFooterLogoKey || '',
		logoUrl: pres?.gammaFooterLogoUrl || '',
		logoSize: pres?.gammaFooterLogoSize ?? 'md',
		hideFirst: pres?.gammaFooterHideFirst ?? false,
		hideLast: pres?.gammaFooterHideLast ?? false,
	}
}

/** Патч настроек из состояния шита. Легаси-поля текста/номера зануляются —
 *  дальше живёт только карта слотов (лого-поля общие, остаются). */
export function hfSettingsPatch(state: GammaHfState): Partial<PresentationSettings> {
	const hasSlots = Object.keys(state.slots).length > 0
	const hasLogoSlot = Object.values(state.slots).some((c) => c?.type === 'logo')
	return {
		gammaHfSlots: hasSlots ? state.slots : undefined,
		gammaFooterText: undefined,
		gammaFooterTextPos: undefined,
		gammaFooterNumber: undefined,
		gammaFooterNumberPos: undefined,
		gammaFooterLogoKey: state.logoKey || undefined,
		gammaFooterLogoUrl: state.logoKey ? state.logoUrl : undefined,
		gammaFooterLogoPos: undefined,
		gammaFooterLogoSize: hasLogoSlot ? state.logoSize : undefined,
		gammaFooterHideFirst: hasSlots ? state.hideFirst || undefined : undefined,
		gammaFooterHideLast: hasSlots ? state.hideLast || undefined : undefined,
	}
}

/** Короткая сводка для строки панели / мобильного ряда: «Лого + Текст ×2 + №». */
export function hfSummaryParts(pres: PresentationSettings | undefined): string[] {
	const { slots } = hfStateFromSettings(pres)
	const contents = Object.values(slots)
	const parts: string[] = []
	if (contents.some((c) => c?.type === 'logo')) parts.push('Лого')
	const texts = contents.filter((c) => c?.type === 'text').length
	if (texts === 1) parts.push('Текст')
	if (texts > 1) parts.push(`Текст ×${texts}`)
	if (contents.some((c) => c?.type === 'number')) parts.push('№')
	return parts
}

/** Payload генерации: слоты как есть (бэк валидирует и собирает headerFooter). */
export function hfPayloadFromSettings(pres: PresentationSettings | undefined): {
	footer_slots?: Record<string, { type: string; value?: string }>
	footer_logo_key?: string
	footer_logo_size?: string
	footer_hide_first?: boolean
	footer_hide_last?: boolean
} {
	const state = hfStateFromSettings(pres)
	const entries = Object.entries(state.slots)
	if (entries.length === 0) return {}
	const footer_slots: Record<string, { type: string; value?: string }> = {}
	let hasLogo = false
	for (const [pos, content] of entries) {
		if (!content) continue
		if (content.type === 'text') footer_slots[pos] = { type: 'text', value: content.value }
		else if (content.type === 'number') footer_slots[pos] = { type: 'number' }
		else {
			// Лого-слот без загруженного файла молча не шлём («включил и пусто»
			// на слайде хуже честного отсутствия).
			if (!state.logoKey) continue
			footer_slots[pos] = { type: 'logo' }
			hasLogo = true
		}
	}
	if (Object.keys(footer_slots).length === 0) return {}
	return {
		footer_slots,
		...(hasLogo ? { footer_logo_key: state.logoKey, footer_logo_size: state.logoSize } : {}),
		...(state.hideFirst ? { footer_hide_first: true } : {}),
		...(state.hideLast ? { footer_hide_last: true } : {}),
	}
}
